import { isEmpty, isNil, omitBy } from 'lodash';
import { useCallback, useState } from 'react';
import { FilterConfigWithValue, FilterData, FilterValue } from '../../types/filter.types';

export type FilterMapper<BE, ExtraFilters = unknown> = ExtraFilters & {
  [key in keyof Omit<BE, keyof ExtraFilters>]: (
    filter: FilterValue<Required<BE>[key] extends Array<any> ? FilterValue<Required<BE>[key][number]>[] : BE[key]>,
  ) => BE[key];
};

export const useFilterMapper = <BE>(mapper: FilterMapper<BE>): ((filters: FilterData<BE>) => Partial<BE>) => {
  const [internalMapper] = useState(mapper);

  return useCallback(
    (filters: FilterData<BE>): Partial<BE> => {
      type RequiredBE = Required<BE>;
      type MapperKeys = keyof typeof internalMapper;

      const mappedFilters = {} as Partial<Record<MapperKeys, RequiredBE[MapperKeys] | Array<RequiredBE[MapperKeys]>>>;
      const keys = Object.keys(filters) as Array<MapperKeys>;
      for (const key of keys) {
        const filter = filters[key];
        const mapperFunc = internalMapper[key] as (
          filter: FilterValue<RequiredBE[typeof key]>,
        ) => RequiredBE[MapperKeys] | Array<RequiredBE[MapperKeys]>;
        if (filter && mapperFunc) {
          mappedFilters[key] = mapperFunc(filter as FilterConfigWithValue<RequiredBE[typeof key]>);
        }
      }
      return omitBy(mappedFilters, (value) =>
        value && typeof value === 'object' ? isEmpty(value) : isNil(value),
      ) as Partial<BE>;
    },
    [internalMapper],
  );
};
